import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, ChevronDown, MapPin, Users, ShieldCheck, Star, Sparkles, Zap, Wallet, Clock, Calendar,
  MessageCircle, Briefcase, Building2, BadgeCheck, FileCheck, Camera, Lock, Heart, Award, LineChart, Phone, Mail,
  Handshake, Home, Wrench, Laptop, Globe, Receipt, UserCheck, HelpCircle, Layers, Target,
} from "lucide-react";

const cx = (...c) => c.filter(Boolean).join(" ");
const ICONS = {
  "map-pin": MapPin, users: Users, "shield-check": ShieldCheck, star: Star, sparkles: Sparkles, zap: Zap, wallet: Wallet,
  clock: Clock, calendar: Calendar, "message-circle": MessageCircle, briefcase: Briefcase, building: Building2,
  "badge-check": BadgeCheck, "file-check": FileCheck, camera: Camera, lock: Lock, heart: Heart, award: Award,
  "line-chart": LineChart, phone: Phone, mail: Mail, handshake: Handshake, home: Home, wrench: Wrench, laptop: Laptop,
  globe: Globe, receipt: Receipt, "user-check": UserCheck, help: HelpCircle, layers: Layers, target: Target,
};
export const Icon = ({ name, className }) => { const C = ICONS[name] || Sparkles; return <C className={className} />; };

const reveal = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, ease: "easeOut" } };
const TONES = { white: "bg-white", light: "bg-slate-50", sand: "bg-[#f5f0e8]", dark: "bg-[#071b22] text-white" };

export function Cta({ cta, mode, variant = "primary", testid }) {
  if (!cta?.label) return null;
  const cls = {
    primary: "bg-[#165a71] text-white hover:bg-[#0f4657] shadow-[0_10px_30px_-10px_rgba(22,90,113,0.6)]",
    light: "bg-white text-[#071b22] hover:bg-[#e8f3f6]",
    ghost: "border border-white/30 text-white hover:bg-white/10",
    outline: "border border-[#071b22]/15 text-[#071b22] hover:border-[#165a71] hover:text-[#165a71]",
  }[variant];
  const inner = <span className={cx("group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors", cls)}>{cta.label}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>;
  if (mode === "edit") return <span data-testid={testid}>{inner}</span>;
  return <Link to={cta.href || "/"} data-testid={testid}>{inner}</Link>;
}

export function SectionHeader({ eyebrow, headline, lede, dark, center }) {
  return (
    <motion.div {...reveal} className={cx("max-w-2xl", center && "mx-auto text-center")}>
      {eyebrow && <div className={cx("text-xs font-bold tracking-[0.22em] uppercase", dark ? "text-[#87bccc]" : "text-[#165a71]")}>{eyebrow}</div>}
      {headline && <h2 className={cx("mt-3 font-display text-3xl md:text-[2.75rem] md:leading-[1.1] font-semibold tracking-tight", dark ? "text-white" : "text-[#071b22]")}>{headline}</h2>}
      {lede && <p className={cx("mt-4 text-base md:text-lg leading-relaxed", dark ? "text-white/70" : "text-[#252A34]/70")}>{lede}</p>}
    </motion.div>
  );
}

export function PageHero({ props = {}, mode }) {
  const { eyebrow, headline, accent, body, image, primary_cta, secondary_cta, highlights = [], badge } = props;
  return (
    <section className="relative -mt-24 pt-24 overflow-hidden bg-[#071b22] text-white" data-testid="page-hero">
      <div className="absolute inset-0 gcw-noise opacity-30" aria-hidden />
      <div className="absolute -top-40 -right-40 h-[32rem] w-[32rem] rounded-full bg-[#165a71]/40 blur-3xl" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 md:py-24 grid gap-12 lg:grid-cols-[1.05fr_1fr] items-center">
        <div>
          {eyebrow && <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-3.5 py-1.5 text-xs font-medium text-white/80"><span className="h-1.5 w-1.5 rounded-full bg-[#5fd3c6]" />{eyebrow}</motion.div>}
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08, duration: 0.6 }} className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]" data-testid="page-hero-headline">
            {headline} {accent && <span className="text-[#87bccc]">{accent}</span>}
          </motion.h1>
          {body && <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16, duration: 0.6 }} className="mt-6 text-base md:text-lg text-white/70 leading-relaxed max-w-xl">{body}</motion.p>}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }} className="mt-9 flex flex-wrap gap-3">
            <Cta cta={primary_cta} mode={mode} variant="light" testid="page-hero-primary" />
            <Cta cta={secondary_cta} mode={mode} variant="ghost" testid="page-hero-secondary" />
          </motion.div>
          {highlights.length > 0 && (
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg border-t border-white/10 pt-8">
              {highlights.map((h, i) => (
                <div key={i}><div className="font-display text-2xl md:text-3xl font-semibold">{h.value}</div><div className="mt-1 text-xs text-white/55 leading-snug">{h.label}</div></div>
              ))}
            </div>
          )}
        </div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="relative">
          <div className="overflow-hidden rounded-[2rem] ring-1 ring-white/10 shadow-2xl">
            {image && <img src={image} alt="" className="w-full aspect-[5/4] object-cover" />}
          </div>
          {badge?.title && (
            <div className="absolute -bottom-6 left-4 md:-left-8 max-w-[260px] rounded-2xl bg-white/90 backdrop-blur-xl text-[#071b22] p-4 shadow-xl ring-1 ring-black/5" data-testid="page-hero-badge">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-[#165a71] text-white grid place-items-center"><Icon name={badge.icon || "shield-check"} className="h-5 w-5" /></div>
                <div><div className="text-sm font-semibold">{badge.title}</div><div className="text-xs text-[#252A34]/65">{badge.body}</div></div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

export function SplitFeature({ props = {}, mode }) {
  const { eyebrow, headline, body, bullets = [], image, reverse, tone = "white", cta } = props;
  return (
    <section className={cx(TONES[tone] || TONES.white, "py-20 md:py-28")} data-testid="split-feature">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-12 lg:gap-20 lg:grid-cols-2 items-center">
        <motion.div {...reveal} className={cx("relative", reverse && "lg:order-2")}>
          <div className="absolute -inset-3 rounded-[2.25rem] bg-gradient-to-br from-[#165a71]/15 to-transparent" aria-hidden />
          {image && <img src={image} alt="" className="relative w-full aspect-[4/3] object-cover rounded-[2rem] shadow-xl" loading="lazy" />}
        </motion.div>
        <div>
          <SectionHeader eyebrow={eyebrow} headline={headline} lede={body} />
          {bullets.length > 0 && (
            <ul className="mt-8 space-y-4">
              {bullets.map((b, i) => (
                <motion.li key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.06 }} className="flex gap-3">
                  <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-[#165a71]/10 text-[#165a71] grid place-items-center"><Check className="h-3.5 w-3.5" /></span>
                  <span className="text-[#252A34]/85 leading-relaxed">{b}</span>
                </motion.li>
              ))}
            </ul>
          )}
          {cta?.label && <div className="mt-10"><Cta cta={cta} mode={mode} testid="split-feature-cta" /></div>}
        </div>
      </div>
    </section>
  );
}

export function FeatureGrid({ props = {} }) {
  const { eyebrow, headline, lede, items = [], columns = 3, tone = "white" } = props;
  const dark = tone === "dark";
  const grid = columns === 2 ? "md:grid-cols-2" : columns === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3";
  return (
    <section className={cx(TONES[tone] || TONES.white, "relative py-20 md:py-28 overflow-hidden")} data-testid="feature-grid">
      {dark && <div className="absolute inset-0 gcw-noise opacity-30" aria-hidden />}
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeader eyebrow={eyebrow} headline={headline} lede={lede} dark={dark} />
        <div className={cx("mt-14 grid gap-5", grid)}>
          {items.map((it, i) => (
            <motion.div key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.05 }}
              className={cx("group rounded-3xl p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1", dark ? "bg-white/[0.04] ring-1 ring-white/10 hover:bg-white/[0.07]" : "bg-white ring-1 ring-slate-200/80 hover:shadow-[0_24px_48px_-24px_rgba(7,27,34,0.25)]")}>
              <div className={cx("h-12 w-12 rounded-2xl grid place-items-center", dark ? "bg-[#87bccc]/15 text-[#87bccc]" : "bg-[#165a71]/10 text-[#165a71]")}><Icon name={it.icon} className="h-5 w-5" /></div>
              <div className={cx("mt-6 font-display text-lg font-semibold", dark ? "text-white" : "text-[#071b22]")}>{it.title}</div>
              <p className={cx("mt-2 text-sm leading-relaxed", dark ? "text-white/65" : "text-[#252A34]/70")}>{it.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function StepsTimeline({ props = {}, mode }) {
  const { eyebrow, headline, lede, steps = [], image, tone = "light", cta } = props;
  return (
    <section className={cx(TONES[tone] || TONES.light, "py-20 md:py-28")} data-testid="steps-timeline">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-14 lg:grid-cols-[1fr_1.1fr] items-start">
        <div className="lg:sticky lg:top-32">
          <SectionHeader eyebrow={eyebrow} headline={headline} lede={lede} />
          {cta?.label && <div className="mt-8"><Cta cta={cta} mode={mode} testid="steps-cta" /></div>}
          {image && <motion.img {...reveal} src={image} alt="" className="mt-10 hidden lg:block w-full aspect-[4/3] object-cover rounded-[2rem] shadow-xl" loading="lazy" />}
        </div>
        <ol className="relative space-y-5">
          <span className="hidden md:block absolute left-[55px] top-10 bottom-10 w-px bg-gradient-to-b from-[#165a71]/40 via-[#165a71]/20 to-transparent" aria-hidden />
          {steps.map((s, i) => (
            <motion.li key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }} className="relative flex gap-6 rounded-3xl bg-white p-6 md:p-7 ring-1 ring-slate-200/80">
              <span className="relative z-10 h-14 w-14 shrink-0 rounded-2xl bg-[#071b22] text-white grid place-items-center font-display text-lg font-semibold">{String(i + 1).padStart(2, "0")}</span>
              <div><div className="font-display text-lg font-semibold text-[#071b22]">{s.title}</div><p className="mt-2 text-sm md:text-[15px] text-[#252A34]/70 leading-relaxed">{s.body}</p></div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function StatsBand({ props = {} }) {
  const { items = [] } = props;
  return (
    <section className="bg-white border-y border-slate-200/70" data-testid="stats-band">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
        {items.map((s, i) => (
          <motion.div key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.05 }} className="border-l-2 border-[#165a71]/20 pl-5">
            <div className="font-display text-3xl md:text-4xl font-semibold text-[#071b22] tracking-tight">{s.value}</div>
            <div className="mt-1 text-sm text-[#252A34]/60">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export function QuoteBand({ props = {} }) {
  const { quote, name, role, image } = props;
  return (
    <section className="bg-[#f5f0e8] py-20 md:py-28" data-testid="quote-band">
      <motion.figure {...reveal} className="mx-auto max-w-4xl px-6 text-center">
        <div className="font-display text-6xl leading-none text-[#165a71]/30" aria-hidden>&ldquo;</div>
        <blockquote className="font-display text-2xl md:text-[2rem] md:leading-snug font-medium text-[#071b22] tracking-tight">{quote}</blockquote>
        <figcaption className="mt-8 inline-flex items-center gap-3">
          {image && <img src={image} alt="" className="h-12 w-12 rounded-full object-cover ring-2 ring-white" />}
          <span className="text-left"><span className="block font-semibold text-[#071b22]">{name}</span><span className="block text-sm text-[#252A34]/60">{role}</span></span>
        </figcaption>
      </motion.figure>
    </section>
  );
}

export function ImageCta({ props = {}, mode }) {
  const { eyebrow, headline, body, image, primary_cta, secondary_cta } = props;
  return (
    <section className="bg-white px-4 md:px-6 py-16 md:py-20" data-testid="image-cta">
      <motion.div {...reveal} className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-[#071b22]">
        {image && <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" loading="lazy" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#071b22] via-[#071b22]/80 to-transparent" aria-hidden />
        <div className="relative px-8 py-16 md:px-16 md:py-24 max-w-2xl text-white">
          {eyebrow && <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#87bccc]">{eyebrow}</div>}
          <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold tracking-tight leading-[1.08]">{headline}</h2>
          {body && <p className="mt-5 text-white/75 text-base md:text-lg leading-relaxed">{body}</p>}
          <div className="mt-9 flex flex-wrap gap-3">
            <Cta cta={primary_cta} mode={mode} variant="light" testid="image-cta-primary" />
            <Cta cta={secondary_cta} mode={mode} variant="ghost" testid="image-cta-secondary" />
          </div>
        </div>
      </motion.div>
    </section>
  );
}

export function FaqSection({ props = {} }) {
  const { eyebrow = "FAQ", headline, items = [] } = props;
  const [open, setOpen] = React.useState(0);
  return (
    <section className="bg-white py-20 md:py-28" data-testid="faq-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-12 lg:grid-cols-[1fr_1.4fr]">
        <SectionHeader eyebrow={eyebrow} headline={headline} />
        <div className="divide-y divide-slate-200 border-y border-slate-200">
          {items.map((it, i) => (
            <div key={i}>
              <button type="button" onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-6 py-6 text-left" data-testid={`faq-item-${i}`}>
                <span className="font-display text-lg font-medium text-[#071b22]">{it.q}</span>
                <span className={cx("h-9 w-9 shrink-0 rounded-full grid place-items-center transition-colors", open === i ? "bg-[#165a71] text-white" : "bg-slate-100 text-[#071b22]")}><ChevronDown className={cx("h-4 w-4 transition-transform", open === i && "rotate-180")} /></span>
              </button>
              {open === i && <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="pb-6 pr-14 text-[#252A34]/75 leading-relaxed">{it.a}</motion.p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ChannelCards({ items = [] }) {
  return (
    <div className="mt-10 grid gap-4 sm:grid-cols-2">
      {items.map((c, i) => (
        <div key={i} className="rounded-2xl bg-white ring-1 ring-slate-200/80 p-5">
          <div className="h-10 w-10 rounded-xl bg-[#165a71]/10 text-[#165a71] grid place-items-center"><Icon name={c.icon} className="h-5 w-5" /></div>
          <div className="mt-4 font-semibold text-[#071b22]">{c.title}</div>
          <div className="mt-1 text-sm text-[#252A34]/65 break-words">{c.body}</div>
        </div>
      ))}
    </div>
  );
}
